import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { ReactNode } from 'react';

interface FunctionBasedRouteProps {
  allowedFunctions?: string[];
  excludedFunctions?: string[];
  children: ReactNode;
}

export default function FunctionBasedRoute({ 
  allowedFunctions, 
  excludedFunctions,
  children 
}: FunctionBasedRouteProps) {
  const { user, profile, isLoading } = useAuth();
  const location = useLocation();

  // Debug logging for SEO access issue
  console.log('[FunctionBasedRoute] Debug:', {
    path: location.pathname,
    user: user?.email,
    profile: profile,
    allowedFunctions,
    excludedFunctions,
    isLoading
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !profile) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user is CupShup role
  if (profile.role !== 'CupShup') {
    return <Navigate to="/cupshup" replace />;
  }

  // Check if user has allowed functions (if specified)
  if (allowedFunctions && profile.function && !allowedFunctions.includes(profile.function)) {
    // Temporary debug - show current function for SEO access issue
    if (location.pathname.includes('/seo')) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
            <h2 className="text-xl font-bold text-red-600 mb-4">SEO Access Denied</h2>
            <div className="text-left space-y-2">
              <p><strong>Current Function:</strong> {profile.function || 'None'}</p>
              <p><strong>Required Functions:</strong> {allowedFunctions.join(', ')}</p>
              <p><strong>User:</strong> {profile.name} ({profile.email})</p>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              Contact your administrator to change your function to access SEO tools.
            </p>
            <button 
              onClick={() => window.location.href = '/cupshup'} 
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      );
    }
    return <Navigate to="/cupshup" replace />;
  }

  // Check if user has excluded functions (if specified)
  if (excludedFunctions && profile.function && excludedFunctions.includes(profile.function)) {
    return <Navigate to="/cupshup" replace />;
  }

  return <>{children}</>;
}

